import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { usersService } from '@/services';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserPlus, RotateCcw, Ban, Trash2, Shield, User, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { User as UserType, AppRole } from '@/types';

const createUserSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  role: z.enum(['admin', 'utilisateur'])
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

interface UserWithDetails extends UserType {
  collaborateur?: {
    nom: string;
    estActif: boolean;
  } | null;
}

export default function GestionUtilisateurs() {
  const { userRole } = useAuth();
  const [users, setUsers] = useState<UserWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithDetails | null>(null);
  const [passwordDialog, setPasswordDialog] = useState<{ open: boolean; userId: string; userEmail: string }>({
    open: false,
    userId: '',
    userEmail: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState<AppRole>('utilisateur');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      role: 'utilisateur'
    }
  });

  useEffect(() => {
    if (userRole === 'admin') {
      loadUsers();
    }
  }, [userRole]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersService.list();
      setUsers(data as UserWithDetails[]);
    } catch (error: any) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (data: CreateUserFormData) => {
    try {
      await usersService.create({
        email: data.email,
        password: data.password,
        role: selectedRole
      });

      toast.success('Utilisateur créé avec succès');
      setIsCreateDialogOpen(false);
      reset();
      setSelectedRole('utilisateur');
      loadUsers();
    } catch (error: any) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
      toast.error(error?.response?.data?.error || 'Erreur lors de la création de l\'utilisateur');
    }
  };

  const handleResetPassword = async (userId: string, userEmail: string) => {
    setPasswordDialog({ open: true, userId, userEmail });
    setNewPassword('');
  };

  const confirmPasswordReset = async () => {
    if (!newPassword.trim()) {
      toast.error('Veuillez saisir un mot de passe');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    try {
      await usersService.updatePassword(passwordDialog.userId, newPassword);
      toast.success('Mot de passe mis à jour avec succès');
      setPasswordDialog({ open: false, userId: '', userEmail: '' });
      setNewPassword('');
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast.error('Erreur lors de la mise à jour du mot de passe');
    }
  };

  const handleToggleBan = async (user: UserWithDetails) => {
    try {
      const isBanned = user.bannedUntil && new Date(user.bannedUntil) > new Date();
      await usersService.updateStatus(user.id, !isBanned);
      toast.success(`Utilisateur ${!isBanned ? 'suspendu' : 'réactivé'} avec succès`);
      loadUsers();
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await usersService.delete(selectedUser.id);
      toast.success('Utilisateur supprimé avec succès');
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const getRoleBadge = (role: AppRole | null) => {
    if (role === 'admin') {
      return <Badge variant="default" className="gap-1"><Shield className="h-3 w-3" />Admin</Badge>;
    }
    return <Badge variant="outline" className="gap-1"><User className="h-3 w-3" />Utilisateur</Badge>;
  };

  const getStatusBadge = (user: UserWithDetails) => {
    const isBanned = user.bannedUntil && new Date(user.bannedUntil) > new Date();
    const isConfirmed = user.emailConfirmedAt;

    if (isBanned) {
      return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Suspendu</Badge>;
    }
    if (!isConfirmed) {
      return <Badge variant="secondary" className="gap-1">Non vérifié</Badge>;
    }
    return <Badge variant="default" className="gap-1 bg-green-500"><CheckCircle className="h-3 w-3" />Actif</Badge>;
  };

  if (userRole !== 'admin') {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Accès refusé</h1>
            <p>Vous devez être administrateur pour accéder à cette page.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Gestion des utilisateurs</h1>
          <p className="text-muted-foreground">
            Gérez les comptes utilisateurs, les rôles et les permissions
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Utilisateurs du système</CardTitle>
                <CardDescription>
                  Liste complète des utilisateurs avec leurs statuts et rôles
                </CardDescription>
              </div>
              <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
                <UserPlus className="h-4 w-4" />
                Nouvel utilisateur
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Chargement...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Collaborateur</TableHead>
                    <TableHead>Dernière connexion</TableHead>
                    <TableHead>Créé le</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => {
                    const isBanned = user.bannedUntil && new Date(user.bannedUntil) > new Date();

                    return (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>{getStatusBadge(user)}</TableCell>
                        <TableCell>{getRoleBadge(user.role)}</TableCell>
                        <TableCell>
                          {user.collaborateur ? (
                            <div className="flex items-center gap-2">
                              <span>{user.collaborateur.nom}</span>
                              {user.collaborateur.estActif ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">Non lié</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.lastSignInAt ?
                            format(new Date(user.lastSignInAt), 'dd/MM/yyyy HH:mm', { locale: fr }) :
                            'Jamais'
                          }
                        </TableCell>
                        <TableCell>
                          {format(new Date(user.createdAt), 'dd/MM/yyyy', { locale: fr })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResetPassword(user.id, user.email)}
                              className="gap-1"
                            >
                              <RotateCcw className="h-3 w-3" />
                              Reset
                            </Button>
                            <Button
                              variant={isBanned ? "default" : "destructive"}
                              size="sm"
                              onClick={() => handleToggleBan(user)}
                              className="gap-1"
                            >
                              <Ban className="h-3 w-3" />
                              {isBanned ? 'Réactiver' : 'Suspendre'}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                setIsDeleteDialogOpen(true);
                              }}
                              className="gap-1"
                            >
                              <Trash2 className="h-3 w-3" />
                              Supprimer
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create User Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un nouvel utilisateur</DialogTitle>
              <DialogDescription>
                Saisissez les informations du nouvel utilisateur
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit(handleCreateUser)} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="utilisateur@exemple.com"
                />
                {errors.email && (
                  <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  {...register('password')}
                  placeholder="Mot de passe (min. 8 caractères)"
                />
                {errors.password && (
                  <p className="text-sm text-destructive mt-1">{errors.password.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="role">Rôle</Label>
                <Select
                  value={selectedRole}
                  onValueChange={(value: AppRole) => setSelectedRole(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="utilisateur">Utilisateur</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit">
                  Créer l'utilisateur
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete User Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer l'utilisateur</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer l'utilisateur {selectedUser?.email} ?
                Cette action est irréversible et supprimera définitivement le compte.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setSelectedUser(null)}>
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Password Reset Dialog */}
        <Dialog open={passwordDialog.open} onOpenChange={(open) => setPasswordDialog({ ...passwordDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier le mot de passe</DialogTitle>
              <DialogDescription>
                Saisissez un nouveau mot de passe pour {passwordDialog.userEmail}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 caractères"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      confirmPasswordReset();
                    }
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPasswordDialog({ open: false, userId: '', userEmail: '' })}>
                Annuler
              </Button>
              <Button onClick={confirmPasswordReset}>
                Mettre à jour
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
